import { useState } from "react";
import type { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { IconArrow, IconClose, IconMenu } from "./Icons";
import { Logo } from "./Logo";

const SECTIONS = [
  { id: "how", label: "How it works" },
  { id: "earnings", label: "Earnings" },
  { id: "stories", label: "Stories" },
  { id: "faq", label: "FAQ" },
];

export function scrollToSection(id: string) {
  const node = document.getElementById(id);
  if (!node) return false;
  node.scrollIntoView({ behavior: "smooth", block: "start" });
  window.history.replaceState(null, "", `/#${id}`);
  return true;
}

function SectionLink({
  id,
  children,
  className,
  onDone,
}: {
  id: string;
  children: ReactNode;
  className?: string;
  onDone?: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <a
      href={`/#${id}`}
      className={className}
      onClick={(event) => {
        event.preventDefault();
        onDone?.();
        if (location.pathname === "/") {
          scrollToSection(id);
          return;
        }
        navigate(`/#${id}`);
      }}
    >
      {children}
    </a>
  );
}

export function MarketingChrome({
  children,
  back,
  footer = "full",
}: {
  children: ReactNode;
  /** Compact header used on forms: logo and a back link. */
  back?: { to: string; label: string; short?: string };
  /** "compact" is the single-line footer on the sign-in screen. */
  footer?: "full" | "compact";
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="site">
      <header className="site-header">
        <div className="site-header-inner">
          <Logo />
          {back ? (
            <Link to={back.to} className="back-link">
              <IconArrow style={{ transform: "scaleX(-1)" }} width={16} height={16} />
              {back.short ? (
                <>
                  <span className="back-full">{back.label}</span>
                  <span className="back-short">{back.short}</span>
                </>
              ) : (
                back.label
              )}
            </Link>
          ) : (
            <>
              <nav className="site-nav" aria-label="Page">
                {SECTIONS.map((section) => (
                  <SectionLink key={section.id} id={section.id}>
                    {section.label}
                  </SectionLink>
                ))}
              </nav>
              <div className="site-actions">
                <Link to="/login" className="btn btn-ghost btn-sm site-signin">
                  Sign in
                </Link>
                <Link to="/apply" className="btn btn-primary btn-sm site-apply">
                  <span className="apply-long">Apply to contribute</span>
                  <span className="apply-short">Apply</span>
                </Link>
              </div>
              <button
                type="button"
                className="menu-button"
                aria-label="Open menu"
                onClick={() => setMenuOpen(true)}
              >
                <IconMenu />
              </button>
            </>
          )}
        </div>
      </header>

      {menuOpen && (
        <div className="mobile-menu" role="dialog" aria-label="Menu">
          <div className="mobile-menu-top">
            <Logo />
            <button type="button" className="icon-button" aria-label="Close menu" onClick={() => setMenuOpen(false)}>
              <IconClose />
            </button>
          </div>
          <nav>
            {SECTIONS.map((section) => (
              <SectionLink
                key={section.id}
                id={section.id}
                className="mobile-menu-link"
                onDone={() => setMenuOpen(false)}
              >
                {section.label}
                <IconArrow width={16} height={16} />
              </SectionLink>
            ))}
          </nav>
          <div className="mobile-menu-actions">
            <Link to="/login" className="btn btn-ghost" onClick={() => setMenuOpen(false)}>
              Sign in
            </Link>
            <Link to="/apply" className="btn btn-primary" onClick={() => setMenuOpen(false)}>
              Apply to contribute <IconArrow width={16} height={16} />
            </Link>
          </div>
        </div>
      )}

      <main>{children}</main>
      {footer === "compact" ? <LoginFooter /> : <SiteFooter />}
    </div>
  );
}

function LoginFooter() {
  return (
    <footer className="login-footer">
      <div className="login-footer-inner">
        <p>© {new Date().getFullYear()} Inputry</p>
        <nav aria-label="Footer">
          <Link to="/terms">
            <span className="login-footer-long">Contributor Terms</span>
            <span className="login-footer-short">Terms</span>
          </Link>
          <Link to="/privacy">
            <span className="login-footer-long">Privacy Policy</span>
            <span className="login-footer-short">Privacy</span>
          </Link>
          <Link to="/contact">Contact</Link>
        </nav>
      </div>
    </footer>
  );
}

export function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="site-footer-inner">
        <div className="footer-main">
          <div className="footer-brand">
            <Logo />
            <p>A paid voice contributor network built around real voices.</p>
          </div>
          <nav className="footer-nav" aria-label="Footer">
            <div>
              <h2>Contribute</h2>
              <Link to="/how-it-works">How it works</Link>
              <Link to="/apply">
                <span className="footer-long">Apply to contribute</span>
                <span className="footer-short">Apply</span>
              </Link>
              <Link to="/login">Sign in</Link>
              <Link to="/signup" className="footer-desktop">Create account</Link>
            </div>
            <div>
              <h2>Support</h2>
              <SectionLink id="faq">FAQ</SectionLink>
              <Link to="/contact">Contact</Link>
            </div>
            <div>
              <h2>Legal</h2>
              <Link to="/terms">
                <span className="footer-long">Contributor Terms</span>
                <span className="footer-short">Terms</span>
              </Link>
              <Link to="/privacy">
                <span className="footer-long">Privacy Policy</span>
                <span className="footer-short">Privacy</span>
              </Link>
            </div>
          </nav>
        </div>
        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} Inputry. All rights reserved.</p>
          <p>United States · English</p>
        </div>
      </div>
    </footer>
  );
}
